import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../utils/api';
import { toast } from 'sonner';
import { FileText, Plus, X } from 'lucide-react';

const API = getBackendUrl();

const AdminDisclaimers = () => {
  const { token } = useAuth();
  const [disclaimers, setDisclaimers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDisclaimer, setEditingDisclaimer] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    content: ''
  });

  useEffect(() => {
    fetchDisclaimers();
  }, []);

  const fetchDisclaimers = async () => {
    try {
      const response = await fetch(`${API}/admin/booking/disclaimers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setDisclaimers(data.disclaimers);
      }
    } catch (error) {
      console.error('Error fetching disclaimers:', error);
      toast.error('Failed to load disclaimers');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingDisclaimer 
        ? `${API}/admin/booking/disclaimers/${editingDisclaimer.id}`
        : `${API}/admin/booking/disclaimers`;
      
      const method = editingDisclaimer ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(editingDisclaimer ? 'Disclaimer updated' : 'New disclaimer version created');
        fetchDisclaimers();
        resetForm();
      } else {
        toast.error(data.detail || 'Failed to save disclaimer');
      }
    } catch (error) {
      console.error('Error saving disclaimer:', error);
      toast.error('Failed to save disclaimer');
    }
  };

  const handleActivate = async (disclaimerId) => {
    try {
      const response = await fetch(`${API}/admin/booking/disclaimers/${disclaimerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: true })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Disclaimer activated');
        fetchDisclaimers();
      } else {
        toast.error(data.detail || 'Failed to activate disclaimer');
      }
    } catch (error) {
      console.error('Error activating disclaimer:', error);
      toast.error('Failed to activate disclaimer');
    }
  };

  const handleEdit = (disclaimer) => {
    setEditingDisclaimer(disclaimer);
    setFormData({
      title: disclaimer.title,
      content: disclaimer.content
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingDisclaimer(null);
    setFormData({ title: '', content: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#5a6f4e] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#c5bfa9]">Disclaimers</h1>
          <p className="text-gray-600 mt-1">Manage the waiver/disclaimer that players must accept before booking</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#3d4a35] hover:bg-[#4a5d42] text-white rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create New Version
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium">How Disclaimers Work</p>
            <p className="mt-1">
              Creating a new disclaimer automatically makes it the active version. All new bookings will 
              require acceptance of the active disclaimer. Existing bookings remain linked to the version 
              they signed.
            </p>
          </div>
        </div>
      </div>

      {/* Disclaimers List */}
      <div className="space-y-4">
        {disclaimers.map((disclaimer) => (
          <div 
            key={disclaimer.id}
            className={`bg-white rounded-lg shadow p-6 border-l-4 ${
              disclaimer.is_active ? 'border-green-500' : 'border-gray-300'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-lg font-semibold text-gray-900">{disclaimer.title}</span>
                  <span className="text-sm text-gray-500">Version {disclaimer.version}</span>
                  {disclaimer.is_active && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                      Active
                    </span>
                  )}
                </div>
                
                <div 
                  className="text-gray-600 text-sm mt-2 max-h-32 overflow-y-auto prose prose-sm"
                  dangerouslySetInnerHTML={{ 
                    __html: disclaimer.content.substring(0, 300) + (disclaimer.content.length > 300 ? '...' : '')
                  }}
                />
                
                <p className="text-xs text-gray-400 mt-3">
                  Created: {new Date(disclaimer.created_at).toLocaleDateString('en-GB')}
                </p>
              </div>
              
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => handleEdit(disclaimer)}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Edit
                </button>
                {!disclaimer.is_active && (
                  <button
                    onClick={() => handleActivate(disclaimer.id)}
                    className="px-3 py-1.5 text-sm text-green-600 hover:text-green-800 border border-green-300 rounded-lg hover:bg-green-50 transition-colors"
                  >
                    Activate
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {disclaimers.length === 0 && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Disclaimers Yet</h3>
            <p className="text-gray-500 mb-4">Create your first disclaimer for players to accept before booking</p>
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-[#3d4a35] hover:bg-[#4a5d42] text-white rounded-lg transition-colors"
            >
              Create Disclaimer
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {editingDisclaimer ? 'Edit Disclaimer' : 'Create New Disclaimer Version'}
                </h2>
                {!editingDisclaimer && (
                  <p className="text-gray-500 text-sm mt-1">
                    This will create a new version and automatically make it active
                  </p>
                )}
              </div>
              <button
                onClick={resetForm}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Participant Waiver & Disclaimer"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
                <p className="text-xs text-gray-500 mb-2">
                  You can use basic HTML for formatting (e.g., &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;li&gt;)
                </p>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  required
                  rows={15}
                  placeholder="Enter the full disclaimer text..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-[#5a6f4e] focus:border-[#5a6f4e] font-mono text-sm"
                />
              </div>

              {/* Preview */}
              {formData.content && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
                  <div 
                    className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-48 overflow-y-auto prose prose-sm text-gray-700"
                    dangerouslySetInnerHTML={{ __html: formData.content }}
                  />
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#3d4a35] hover:bg-[#4a5d42] text-white rounded-lg transition-colors"
                >
                  {editingDisclaimer ? 'Save Changes' : 'Create Disclaimer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDisclaimers;
